@let current = record();
<header class="dialog-head">
  <div class="dialog-title">
    <p class="eyebrow">{{ label(current).toUpperCase() }}</p>
    <h2>{{ title() }}</h2>
    <a
      class="mono detail-sub"
      [href]="wrpLink(current.wrpId)"
      (click)="close()"
      >{{ current.wrpId }}</a
    >
  </div>
  <button
    type="button"
    class="icon-button"
    aria-label="Close"
    (click)="close()"
  >
    <mat-icon aria-hidden="true">close</mat-icon>
  </button>
</header>

<mat-dialog-content class="dialog-body">
  <div class="dialog-status">
    <span class="badge" [class.muted-badge]="current.revoked">{{
      current.revoked ? "revoked · " + reason(current.revoked) : "unrevoked"
    }}</span>
    <span class="signature" [attr.data-signature]="current.signature">{{
      signatureLabels[current.signature]
    }}</span>
    <div class="chips" role="group" aria-label="Representation">
      <button
        type="button"
        class="chip"
        [class.active]="mode() === 'decoded'"
        [attr.aria-pressed]="mode() === 'decoded'"
        (click)="mode.set('decoded')"
      >
        Decoded
      </button>
      <button
        type="button"
        class="chip"
        [class.active]="mode() === 'published'"
        [attr.aria-pressed]="mode() === 'published'"
        (click)="mode.set('published')"
      >
        As published
      </button>
    </div>
  </div>

  @if (mode() === "decoded") {
    <dl class="facts">
      <dt>Fingerprint</dt>
      <dd class="mono">{{ current.fingerprint }}</dd>
      <dt>Issued</dt>
      <dd>
        <a [href]="eventLink(current.issued)" target="_blank" rel="noopener"
          >#{{ current.issued.sequence_number }}</a
        >
        · {{ current.issued.published_at | date: "medium" }}
      </dd>
      @if (current.revoked; as revoked) {
        <dt>Revoked</dt>
        <dd>
          <a [href]="eventLink(revoked)" target="_blank" rel="noopener"
            >#{{ revoked.sequence_number }}</a
          >
          ·
          {{ revoked.effective_at || revoked.published_at | date: "medium" }}
          · {{ reason(revoked) }}
        </dd>
      }
      @if (previous(); as previous) {
        <dt>Replaces</dt>
        <dd>
          <button
            type="button"
            class="link-button inline"
            (click)="show(previous.fingerprint)"
          >
            Certificate issued #{{ previous.issued.sequence_number }}
          </button>
        </dd>
      }
      @if (next(); as next) {
        <dt>Replaced by</dt>
        <dd>
          <button
            type="button"
            class="link-button inline"
            (click)="show(next.fingerprint)"
          >
            Certificate issued #{{ next.issued.sequence_number }}
          </button>
        </dd>
      }
      @if (current.intendedUseId; as intendedUse) {
        <dt>Intended use</dt>
        <dd class="mono">
          <a
            [href]="intendedUseLink(intendedUse)"
            target="_blank"
            rel="noopener"
            >{{ intendedUse }}</a
          >
        </dd>
      }
    </dl>

    @if (previous()) {
      <h3>Changes from the replaced certificate</h3>
      @if (changes().length) {
        <table class="changes">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Before</th>
              <th scope="col">After</th>
            </tr>
          </thead>
          <tbody>
            @for (change of changes(); track change.field) {
              <tr>
                <th scope="row" class="mono">{{ change.field }}</th>
                <td>
                  <pre>{{ change.before ?? "—" }}</pre>
                </td>
                <td>
                  <pre>{{ change.after ?? "—" }}</pre>
                </td>
              </tr>
            }
          </tbody>
        </table>
      } @else {
        <p class="muted small">
          Same content as the replaced certificate; only issuance data such as
          time, identifier and status-list entry differ.
        </p>
      }
    }

    @if (registration(); as registration) {
      <h3>Purpose</h3>
      @if (registration.purpose.length) {
        <ul class="plain-list">
          @for (entry of registration.purpose; track $index) {
            <li>
              @if (entry.lang) {
                <span class="lang">{{ entry.lang }}</span>
              }
              {{ entry.value }}
            </li>
          }
        </ul>
      } @else {
        <p class="muted small">No purpose given.</p>
      }

      <h3>Requested data</h3>
      @for (credential of registration.credentials; track $index) {
        <div class="credential">
          <div class="credential-head">
            <span class="format">{{ credential.format }}</span>
            @for (type of credential.types; track type) {
              <a class="mono" [href]="typeLink(type)" (click)="close()">{{
                type
              }}</a>
            } @empty {
              <span class="muted">no attestation type</span>
            }
          </div>
          @if (credential.claims; as claims) {
            <ul class="claim-list">
              @for (claim of claims; track $index) {
                <li class="mono">
                  {{ claim.normalizedPath }}
                  @if (claim.values) {
                    <span class="muted">= {{ claim.values.join(", ") }}</span>
                  }
                </li>
              }
            </ul>
          } @else {
            <p class="muted small">
              No claims declared: the registration does not list individual
              attributes for this credential.
            </p>
          }
        </div>
      } @empty {
        <p class="muted small">No credentials requested.</p>
      }

      @if (registration.provides.length) {
        <h3>Provides attestations</h3>
        <ul class="plain-list">
          @for (attestation of registration.provides; track $index) {
            <li>
              <span class="format">{{ attestation.format }}</span>
              <span class="mono">{{
                attestation.types.join(", ") || "no attestation type"
              }}</span>
            </li>
          }
        </ul>
      }

      <h3>Wallet-relying party</h3>
      <dl class="facts">
        <dt>Name</dt>
        <dd>{{ registration.name ?? "—" }}</dd>
        @if (registration.legalName) {
          <dt>Legal name</dt>
          <dd>{{ registration.legalName }}</dd>
        }
        <dt>Identifier</dt>
        <dd class="mono">{{ registration.identifier ?? "—" }}</dd>
        @if (registration.country) {
          <dt>Country</dt>
          <dd>{{ registration.country }}</dd>
        }
        <dt>Entitlements</dt>
        <dd>
          @for (
            entitlement of registration.entitlements;
            track entitlement.uri
          ) {
            <span [title]="entitlement.uri">{{ entitlement.label }}</span
            >{{ $last ? "" : ", " }}
          } @empty {
            —
          }
        </dd>
        @if (registration.publicBody !== undefined) {
          <dt>Public body</dt>
          <dd>{{ registration.publicBody ? "yes" : "no" }}</dd>
        }
        @if (registration.intermediary; as intermediary) {
          <dt>Intermediary</dt>
          <dd>
            {{ intermediary.name ?? "—" }}
            <span class="mono">{{ intermediary.sub }}</span>
          </dd>
        }
        @if (registration.serviceDescriptions.length) {
          <dt>Service</dt>
          <dd>
            @for (entry of registration.serviceDescriptions; track $index) {
              <div>
                @if (entry.lang) {
                  <span class="lang">{{ entry.lang }}</span>
                }
                {{ entry.value }}
              </div>
            }
          </dd>
        }
        @if (registration.privacyPolicy; as uri) {
          <dt>Privacy policy</dt>
          <dd>
            <a [href]="uri" target="_blank" rel="noopener noreferrer">{{
              uri
            }}</a>
          </dd>
        }
        @if (registration.supportUri; as uri) {
          <dt>Support</dt>
          <dd>{{ uri }}</dd>
        }
        @if (registration.infoUri; as uri) {
          <dt>Information</dt>
          <dd>{{ uri }}</dd>
        }
        @if (registration.supervisoryAuthority.length) {
          <dt>Supervisory authority</dt>
          <dd>{{ registration.supervisoryAuthority.join(" · ") }}</dd>
        }
        @if (registration.registryUri; as uri) {
          <dt>Registry</dt>
          <dd>{{ uri }}</dd>
        }
      </dl>

      <h3>Certificate</h3>
      <dl class="facts">
        <dt>Issued at (iat)</dt>
        <dd>{{ (registration.issuedAt | date: "medium") ?? "not given" }}</dd>
        <dt>Expires (exp)</dt>
        <dd>{{ (registration.expiresAt | date: "medium") ?? "not given" }}</dd>
        @if (registration.statusList; as statusList) {
          <dt>Status list</dt>
          <dd>{{ statusList }}</dd>
        }
        @if (registration.jti; as jti) {
          <dt>Identifier (jti)</dt>
          <dd class="mono">{{ jti }}</dd>
        }
      </dl>

      @if (unknownFields().length) {
        <p class="note">
          <mat-icon aria-hidden="true">info</mat-icon>
          <span
            >Fields that TS 119 475 v1.2.1 does not define and the register
            ignores:
            <span class="mono">{{ unknownFields().join(", ") }}</span></span
          >
        </p>
      }
    }

    @if (current.access; as access) {
      <h3>Subject</h3>
      <dl class="facts">
        <dt>Common name</dt>
        <dd>{{ access.commonName ?? "—" }}</dd>
        @if (access.organizationName) {
          <dt>Organization</dt>
          <dd>{{ access.organizationName }}</dd>
        }
        <dt>Identifier</dt>
        <dd class="mono">{{ access.organizationIdentifier ?? "—" }}</dd>
        @if (access.country) {
          <dt>Country</dt>
          <dd>{{ access.country }}</dd>
        }
        @if (access.uris.length) {
          <dt>URIs</dt>
          <dd>{{ access.uris.join(", ") }}</dd>
        }
        @if (access.emails.length) {
          <dt>Email</dt>
          <dd>{{ access.emails.join(", ") }}</dd>
        }
        @if (access.phones.length) {
          <dt>Phone</dt>
          <dd>{{ access.phones.join(", ") }}</dd>
        }
        @if (access.dnsNames.length) {
          <dt>DNS names</dt>
          <dd>{{ access.dnsNames.join(", ") }}</dd>
        }
      </dl>
      <h3>Certificate</h3>
      <dl class="facts">
        <dt>Issuer</dt>
        <dd>{{ access.issuer ?? "—" }}</dd>
        <dt>Serial number</dt>
        <dd class="mono">{{ access.serialNumber }}</dd>
        <dt>Valid from</dt>
        <dd>{{ (access.notBefore | date: "medium") ?? "—" }}</dd>
        <dt>Valid until</dt>
        <dd>{{ (access.notAfter | date: "medium") ?? "—" }}</dd>
        @if (access.policies.length) {
          <dt>Policies</dt>
          <dd class="mono">{{ access.policies.join(", ") }}</dd>
        }
        <dt>Public key</dt>
        <dd class="mono">{{ access.publicKeyFingerprint }}</dd>
      </dl>
    }

    @if (current.parseError) {
      <p class="note error-text">
        <mat-icon aria-hidden="true">error</mat-icon>
        <span>Unreadable: {{ current.parseError }}</span>
      </p>
    }
  } @else {
    @for (part of published().parts; track part.label) {
      <h3>{{ part.label }}</h3>
      <pre class="published">{{ part.text }}</pre>
    }
    @if (published().encoded; as encoded) {
      <details class="compact">
        <summary>Compact JWS</summary>
        <pre class="published">{{ encoded }}</pre>
      </details>
    }
    <button type="button" class="chip copy" (click)="copy()">
      <mat-icon aria-hidden="true">{{
        copied() ? "check" : "content_copy"
      }}</mat-icon>
      {{ copied() ? "Copied" : "Copy as published" }}
    </button>
  }
</mat-dialog-content>
